if (!customElements.get('product-specification-tabs')) {
  class ProductSpecificationTabs extends HTMLElement {
    connectedCallback() {
      this.tablist = this.querySelector('[data-product-spec-tablist]');
      this.panelsRoot = this.querySelector('[data-product-spec-panels]');
      if (!this.tablist || !this.panelsRoot) return;

      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleTabKeydown = this.handleTabKeydown.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.buildTabs();
      this.bindDesignModeEvents();
    }

    disconnectedCallback() {
      this.tabs?.forEach((tab) => {
        tab.removeEventListener('click', this.handleTabClick);
        tab.removeEventListener('keydown', this.handleTabKeydown);
      });
      if (this._designModeBound) {
        this.removeEventListener('shopify:block:select', this.onBlockSelect);
        this._designModeBound = false;
      }
    }

    get isShopifyDesignMode() {
      return typeof Shopify !== 'undefined' && Shopify.designMode;
    }

    bindDesignModeEvents() {
      if (!this.isShopifyDesignMode || this._designModeBound) return;
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this._designModeBound = true;
    }

    onBlockSelect(event) {
      const panel = event.target.closest('[data-tab-content]');
      if (!panel || !this.contains(panel)) return;
      const index = panel.dataset.index;
      if (index == null || index === '') return;
      this.showTab(index);
    }

    buildTabs() {
      const panels = Array.from(this.panelsRoot.querySelectorAll('[data-tab-content]'));
      this.tablist.replaceChildren();

      if (!panels.length) {
        this.tablist.closest('.product-specification-info-tabs')?.classList.add('hidden');
        return;
      }

      this.tablist.closest('.product-specification-info-tabs')?.classList.remove('hidden');

      panels.forEach((panel, index) => {
        const tabIndex = String(index + 1);
        const label = panel.dataset.tabLabel || `Tab ${tabIndex}`;
        const panelId = `product-specification-tab-content-${tabIndex}-${this.id}`;
        const tabId = `product-specification-info-${tabIndex}-${this.id}`;

        panel.id = panelId;
        panel.dataset.index = tabIndex;
        panel.setAttribute('aria-labelledby', tabId);

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'tab-item product-specification-info-tab-item flex gap-1 items-center justify-center';
        button.id = tabId;
        button.dataset.tabItem = '';
        button.dataset.index = tabIndex;
        button.setAttribute('aria-controls', panelId);
        button.setAttribute('aria-selected', 'false');
        button.tabIndex = -1;
        const nameSpan = document.createElement('span');
        nameSpan.className = 'tab-item-name';
        nameSpan.dataset.tabItemName = '';
        nameSpan.textContent = label;
        button.appendChild(nameSpan);
        button.addEventListener('click', this.handleTabClick);
        button.addEventListener('keydown', this.handleTabKeydown);
        this.tablist.appendChild(button);
      });

      this.tabs = this.tablist.querySelectorAll('[data-tab-item]');
      this.tabsContent = panels;
      this.tabsArray = Array.from(this.tabs);

      if (this.tabsArray.length) {
        this.showTab(this.tabsArray[0].dataset.index);
      }
    }

    showTab(index) {
      if (index == null) return;

      this.tabsArray.forEach((tab) => {
        const isActive = tab.dataset.index === index;
        tab.classList.toggle('active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.tabsContent.forEach((content) => {
        const isActive = content.dataset.index === index;
        if (isActive) {
          content.removeAttribute('hidden');
          content.setAttribute('aria-hidden', 'false');
          content.setAttribute('tabindex', '0');
        } else {
          content.setAttribute('hidden', '');
          content.setAttribute('aria-hidden', 'true');
          content.setAttribute('tabindex', '-1');
        }
      });
    }

    handleTabClick(event) {
      event.preventDefault();
      const index = event.currentTarget.dataset.index;
      if (index == null) return;
      this.showTab(index);
    }

    handleTabKeydown(event) {
      const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!keys.includes(event.key) || !this.tabsArray.length) return;

      event.preventDefault();
      const currentIndex = this.tabsArray.indexOf(event.currentTarget);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % this.tabsArray.length;
      } else if (event.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + this.tabsArray.length) % this.tabsArray.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.tabsArray.length - 1;
      }

      const nextTab = this.tabsArray[nextIndex];
      if (!nextTab) return;
      nextTab.focus();
      this.showTab(nextTab.dataset.index);
    }
  }

  customElements.define('product-specification-tabs', ProductSpecificationTabs);
}

if (!customElements.get('product-specification-highlights')) {
  class ProductSpecificationHighlights extends HTMLElement {
    connectedCallback() {
      const max = Number(this.dataset.maxHighlights || 3);
      const highlights = this.querySelectorAll('[data-product-spec-highlight]');
      highlights.forEach((highlight, index) => {
        if (index >= max) highlight.hidden = true;
      });
    }
  }

  customElements.define('product-specification-highlights', ProductSpecificationHighlights);
}
